import type { Me, TeamRosterPlayerRead } from '@volee/api-client'

import { servedClubsOf } from './club.ts'
import { relationOf, type Relation } from './convocati.ts'
import { matchesName } from './roster-filters.ts'
import type { SquadRef } from './viewer.ts'
import { messages } from '../messages/index.ts'

/** The two filters of the working rosa besides the search: who is over the annata, and who has no
 *  account. Named once so the `filtro` of the address cannot hold a third. */
export const ROSTER_FILTERS = ['fuori-quota', 'senza-account'] as const

export type RosterFilter = (typeof ROSTER_FILTERS)[number]

/** The rosa a reader sees: narrowed by the search, and by the filter if one is on. */
export interface RosterNarrowing {
  readonly q: string
  readonly filtro: RosterFilter | null
}

type RosterRule = (player: TeamRosterPlayerRead) => boolean

/** What each filter keeps: said once, for the narrowing and for the count in its chip. */
const FILTER_RULES: Record<RosterFilter, RosterRule> = {
  'fuori-quota': (player) => player.over_age,
  'senza-account': (player) => !player.has_account,
}

export function narrowRoster(
  players: readonly TeamRosterPlayerRead[],
  { q, filtro }: RosterNarrowing,
): readonly TeamRosterPlayerRead[] {
  return players.filter(
    (player) => matchesName(player, q) && (filtro === null || FILTER_RULES[filtro](player)),
  )
}

/** How many players a filter leaves, for its chip: counted on the whole rosa and not on what the
 *  search leaves, so a chip does not change under the finger that is typing. */
export function filterCount(
  players: readonly TeamRosterPlayerRead[],
  filter: RosterFilter,
): number {
  return players.filter((player) => FILTER_RULES[filter](player)).length
}

// What a reader of a squad's rosa is told about each player: whose card is theirs, and whose profile
// they may open. Both are read from `me` in the client, as the API answers a rosa with no such thing.

/** The word on a card that is the reader's own, or their child's — the one they follow included, who is
 *  read as a child. Nothing on anyone else's. */
export function ownLabelOf(relation: Relation): string | undefined {
  if (relation === 'self') return messages.match.callUp.you
  return relation === null ? undefined : messages.match.callUp.yourChild
}

/** Whether the reader works in the società: Volée staff, or a seat there. A seat, and not an incarico:
 *  staff without one is in none, as the server has it. */
export function worksInTheClub(me: Me, clubId: number): boolean {
  return me.staff_role !== null || servedClubsOf(me).some((club) => club.id === clubId)
}

/** Whoever works in the società opens every profile; a member, their own and their child's. Narrower
 *  than `require_may_read_person`, which may also let a public profile through: the rosa promises none. */
export function opensPlayerProfile(me: Me | null, squad: SquadRef, personId: number): boolean {
  if (me === null) return false
  return worksInTheClub(me, squad.club_id) || relationOf(personId, me) !== null
}
